import dayjs, { Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { i18n } from "@libs/i18n";
import { ProvinceType } from "@/domain/location";

export type DateType = string | Dayjs | Date;

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

export const ProvinceToTimezone: Record<string, string> = {
  ON: "America/Toronto",
  QC: "America/Toronto",
  NS: "America/Halifax",
  NB: "America/Moncton",
  MB: "America/Winnipeg",
  BC: "America/Vancouver",
  AB: "America/Edmonton",
  SK: "America/Regina",
  NL: "America/St_Johns",
  PE: "America/Halifax",
  YT: "America/Whitehorse",
  NT: "America/Yellowknife",
  NU: "America/Iqaluit",
};

export const dateWithTimezone = ({
  date,
  province,
  keepTime = false,
}: {
  date: DateType;
  province: ProvinceType;
  keepTime?: boolean;
}): Dayjs => {
  const timezone = ProvinceToTimezone[province] ?? "America/Toronto";

  return dayjs(date).tz(timezone, keepTime);
};

export const dateAsDayjs = (date?: DateType | null): Dayjs => {
  return dayjs(date);
};

export const getDateWithoutTimezone = (date?: DateType) => {
  return dayjs.utc(date);
};

export const getDateWithTimezone = (date: DateType, timezone: string) => {
  return dayjs(date).tz(timezone);
};

export const formatDateToIso = (date: DateType) => {
  return dateAsDayjs(date).toISOString();
};

export const formatMinutesToHours = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = minutes / 60;
  return hours % 1 === 0 ? `${hours}h` : `${parseFloat(hours.toFixed(1))}h`;
};

export const timezoneOptions = [
  {
    value: Intl.DateTimeFormat().resolvedOptions().timeZone,
    label: i18n.t("main:calendars.timezones.local"),
  },
  {
    value: "America/New_York",
    label: i18n.t("main:calendars.timezones.eastern"),
  },
  {
    value: "America/Halifax",
    label: i18n.t("main:calendars.timezones.atlantic"),
  },
];

export const sortDatesAroundToday = (
  dateKeys: string[],
  format = "MMMM DD, YYYY",
): string[] => {
  const today = dayjs().startOf("day");

  return [...dateKeys].sort((a, b) => {
    const dayA = dayjs(a, format).startOf("day");
    const dayB = dayjs(b, format).startOf("day");

    const isAPast = dayA.isBefore(today);
    const isBPast = dayB.isBefore(today);
    const isAFuture = dayA.isAfter(today);
    const isBFuture = dayB.isAfter(today);

    if (isAPast && isBPast) {
      // Both in the past: more recent date first
      return dayB.valueOf() - dayA.valueOf();
    }
    if (isAFuture && isBFuture) {
      // Both in the future: closer date first
      return dayA.valueOf() - dayB.valueOf();
    }
    if (dayA.isSame(today)) return -1;
    if (dayB.isSame(today)) return 1;

    // Past comes before today, today before future
    if (isAPast && !isBPast) return -1;
    if (!isAPast && isBPast) return 1;

    return 0; // Should not happen, fallback
  });
};

export const getTermFromDate = (dateString: DateType) => {
  const date = dayjs(dateString);

  if (!date.isValid()) return "";

  const month = date.month() + 1; // month() returns 0-11

  if (month >= 1 && month <= 4) return "W"; // Winter: Jan-Apr
  if (month >= 5 && month <= 8) return "S"; // Summer: May-Aug
  if (month >= 9 && month <= 12) return "F"; // Fall: Sep-Dec

  return "";
};
